/**
 * check_overlaps' verdicts: given every page's parts at rest, the faults each
 * page shows, deduplicated across pages, and the moves that fix them. Pure: the
 * geometry comes from trace-viewer/build, the page walk from fit-check.ts.
 */
import { contact, studGrid, sweepContact, type FitContact, type FitOverlap, type FitPart, type FitPose, type StudGrid, type Vec3 } from 'trace-viewer/build';

/** A part as one page shows it, and how a move in the frame reads in its parent. */
export interface Placed {
    readonly pose: FitPose;
    /** Frame-to-parent linear map, row-vector layout (3 by 3, row-major). */
    readonly toParent: readonly number[];
    /** Its position in its parent. */
    readonly position: Vec3;
}

export interface FitInput<K> {
    /** Each page's parts, in page order; one entry for a scene without pages. */
    readonly pages: readonly ReadonlyMap<K, Placed>[];
    /** The parts a deck shows while it moves between pages (`walkDeck`); the others are cues. Unset: every part counts. */
    readonly moving?: ReadonlySet<K>;
    /** How a part is named to the reader: a path that resolves to it. */
    readonly path: (part: K) => string;
    /** Only report what this page (0-based) shows. */
    readonly only?: number;
}

/** A move along one frame axis: `sign` and `mm` in the frame, `by` and `position` in the part's parent. */
export interface Move { readonly by: Vec3; readonly position: Vec3; readonly mm: number; readonly axis: number; readonly sign: number }
/** `whole`: sunk by its own full thickness along the overlap's axis: placed by its origin where a face should meet, a build's anchor or origin slip. */
export interface Sunk<K> { readonly moved: K; readonly into: K; readonly overlap: FitOverlap; readonly pages: number[]; readonly move: Move | null; readonly flush: boolean; readonly whole: boolean }
export interface Flicker<K> { readonly a: K; readonly b: K; readonly area: number; readonly pages: number[]; readonly move: Move | null }
/**
 * `through`: the part under it runs through it (a rim in a tyre, a pin in a hole), so dropping onto it is no fix.
 * `side`: the nearest part on another side, within half its size and nearer than what is under it, which may hold it there (a screw driven sideways).
 * `move` seats a lone part; `moves` seat a group of several, one per part, all by the same distance in the frame.
 */
export interface Floating<K> {
    readonly parts: K[]; readonly pages: number[]; readonly under: K | null; readonly drop: number; readonly move: Move | null; readonly moves: Move[] | null; readonly through: boolean;
    readonly side: { readonly part: K; readonly gap: number; readonly axis: number; readonly sign: number; readonly move: Move | null; readonly moves: Move[] | null } | null;
}
export interface Clash<K> { readonly hovering: K; readonly other: K; readonly depth: number; readonly pages: number[] }
/** Far larger than the rest of the model (an 8 m glue tube by a 1 m rocket): a scale or unit slip. `across` and `rest` are largest extents. */
export interface Oversized<K> { readonly part: K; readonly across: number; readonly rest: number; readonly pages: number[] }
export interface ByDesign<K> { readonly a: K; readonly b: K; readonly kind: 'embedded' | 'insertion' | 'surface'; readonly pages: number[] }

export interface FitFindings<K> {
    readonly size: number;
    readonly stud: StudGrid | null;
    readonly studded: number;
    readonly parts: number;
    readonly skipped: { readonly cues: number; readonly hovering: number; readonly open: K[] };
    readonly sunk: Sunk<K>[];
    readonly flicker: Flicker<K>[];
    readonly floating: Floating<K>[];
    readonly clash: Clash<K>[];
    readonly byDesign: ByDesign<K>[];
    readonly oversized: Oversized<K>[];
    readonly firstPage: ReadonlyMap<K, number>;
    /** The last page each part shows on; a cue is on neither map. */
    readonly lastPage: ReadonlyMap<K, number>;
}

const extent = (p: FitPart): Vec3 => [p.hi[0] - p.lo[0], p.hi[1] - p.lo[1], p.hi[2] - p.lo[2]];
const volume = (p: FitPart): number => { const e = extent(p); return e[0] * e[1] * e[2]; };
const thin = (p: FitPart): number => Math.min(p.body.hi[0] - p.body.lo[0], p.body.hi[1] - p.body.lo[1], p.body.hi[2] - p.body.lo[2]);
const upAxis = (p: FitPart): number => [0, 1, 2].reduce((m, k) => (Math.abs(p.up[k]) > Math.abs(p.up[m]) ? k : m), 0);
const boxGap = (a: FitPart, b: FitPart): number => Math.hypot(...[0, 1, 2].map(k => Math.max(0, a.lo[k] - b.hi[k], b.lo[k] - a.hi[k])));
const isSlab = (p: FitPart, size: number): boolean => { const e = extent(p).sort((x, y) => x - y); return e[0] < 0.1 * e[2] && e[1] >= 0.3 * e[2] && e[2] >= 0.8 * size; };
const MOVE_MIN_MM = 0.05;
/** A part this many times the largest extent of the rest of the model (at least 3 other parts) is out of scale: a unit slip is 10x or more. */
const OVERSIZE = 3;
const across = (p: FitPart): number => Math.max(...extent(p));

/**
 * Every fault the pages show, with the move that fixes each: the move seat makes, so the check and seat
 * agree. In a deck a finding is judged on the first page that shows it, so later steps' parts are not in the way,
 * as for seat; a scene without pages counts every part. A part rests where the last page that shows it has it:
 * a second model the last page hides, an alternative or a sheet used up on the way are checked on their own
 * pages. A part shown on one page only, before the last, never comes to rest: it only hovers there.
 */
export function findFit<K>(input: FitInput<K>): FitFindings<K> {
    const { pages, moving } = input;
    const n = pages.length;
    const firstPage = new Map<K, number>(), lastPage = new Map<K, number>();
    // Shown only at rest on a page, never in a move: a cue (an arrow, a tool, a tag), not a part of the model.
    const cues = new Set<K>();
    pages.forEach((page, k) => {
        for (const p of page.keys()) {
            if (moving && !moving.has(p)) { cues.add(p); continue; }
            if (!firstPage.has(p)) firstPage.set(p, k);
            lastPage.set(p, k);
        }
    });
    const rest = new Map([...lastPage].map(([p, k]) => [p, pages[k].get(p)!]));
    const settled = (p: K): boolean => lastPage.get(p)! > firstPage.get(p)! || lastPage.get(p) === n - 1;
    const last = new Map([...rest].filter(([p]) => settled(p)));
    const finalParts = [...last.values()].map(v => v.pose.part);
    const union = (parts: FitPart[]): number => {
        if (!parts.length) return 0;
        const lo = [0, 1, 2].map(k => Math.min(...parts.map(p => p.lo[k]))), hi = [0, 1, 2].map(k => Math.max(...parts.map(p => p.hi[k])));
        return Math.max(hi[0] - lo[0], hi[1] - lo[1], hi[2] - lo[2]);
    };
    const all = union(finalParts);
    const slabs = new Set([...last].filter(([, v]) => isSlab(v.pose.part, all)).map(([p]) => p));
    // Out of scale: far larger than the rest of the model, cues and one-page parts included (a glue tube shown on its page). Each
    // settled part is measured against the others; it is left out of the model's size, so it skews no tolerance.
    const modelEntries = [...last].filter(([p]) => !slabs.has(p));
    const restOf = leaveOneOut(modelEntries.map(([, v]) => v.pose.part));
    const oversized = new Map<K, Oversized<K>>();
    modelEntries.forEach(([p, v], i) => { const a = across(v.pose.part); if (modelEntries.length > 3 && restOf[i] > 0 && a > OVERSIZE * restOf[i]) oversized.set(p, { part: p, across: a, rest: restOf[i], pages: [] }); });
    const model = modelEntries.filter(([p]) => !oversized.has(p)).map(([, v]) => v.pose.part);
    const size = union(model) || all;
    pages.forEach((page, k) => {
        for (const [p, v] of page) {
            if (slabs.has(p) || (last.has(p) && !oversized.has(p))) continue;
            const a = across(v.pose.part);
            if (modelEntries.length < 3 || a <= OVERSIZE * size) continue;
            const found = oversized.get(p);
            if (found) { if (found.pages[found.pages.length - 1] !== k) found.pages.push(k); } else oversized.set(p, { part: p, across: a, rest: size, pages: [k] });
        }
    });
    const grid = studGrid(model), stud = grid?.height ?? null;
    const index = new Map([...new Set(pages.flatMap(page => [...page.keys()]))].map((p, i) => [p, i]));
    // A measured overlap (sampled depths run up to one step deep) counts when deeper than 15% of the
    // thinner part, and in a stud-built model than 90% of a stud. A seated stud the core recognises as a
    // piece of its own never counts; studs welded to their body can only be allowed for as depth.
    const welded = (p: FitPart): number => (p.knob !== null && !p.knobs.some(k => k.axis === upAxis(p)) ? p.knob : 0);
    const tol = (a: FitPart, b: FitPart) => {
        const t = Math.min(thin(a), thin(b));
        const step = Math.min(stud ? stud / 3 : Infinity, Math.max(0.05 * t, 1e-4 * size));
        return { depth: Math.max(welded(a) * 1.25, welded(b) * 1.25, 0.15 * t, stud ? 0.9 * stud : 0), step, reach: Math.max(0.05 * t, 1e-4 * size), area: Math.max(0.5 * t, 0.005 * size) ** 2 };
    };
    const cache = new Map<string, FitContact>();
    const meet = (x: K, a: FitPose, y: K, b: FitPose): FitContact => {
        const [k1, k2] = index.get(x)! < index.get(y)! ? [`${index.get(x)}:${a.key}`, `${index.get(y)}:${b.key}`] : [`${index.get(y)}:${b.key}`, `${index.get(x)}:${a.key}`];
        const key = `${k1}|${k2}`;
        let c = cache.get(key);
        if (!c) { const t = tol(a.part, b.part); c = contact(a.part, b.part, { step: t.step, reach: t.reach }); cache.set(key, c); }
        return c;
    };
    // Without a stud grid a knob seated in a part is a pin or a claw: by design, and listed.
    const engagement = (o: FitOverlap): ByDesign<K>['kind'] | null => (o.engagement === 'stud' ? (stud ? null : 'insertion') : o.engagement);
    const deep = (o: FitOverlap | null, a: FitPart, b: FitPart): boolean => !!o && !(o.engagement === 'stud' && stud) && o.depth > tol(a, b).depth;

    const sunk = new Map<string, Sunk<K>>();
    const flicker = new Map<string, Flicker<K>>();
    const floating = new Map<string, Floating<K>>();
    const clash = new Map<string, Clash<K>>();
    const byDesign = new Map<string, ByDesign<K>>();
    const open = new Set<K>();
    const still = new Set<string>();
    const pairKey = (x: K, y: K): string => (index.get(x)! < index.get(y)! ? `${index.get(x)}|${index.get(y)}` : `${index.get(y)}|${index.get(x)}`);
    const add = <T extends { pages: number[] }>(map: Map<string, T>, key: string, k: number, make: () => T): void => {
        const found = map.get(key);
        if (found) { if (found.pages[found.pages.length - 1] !== k) found.pages.push(k); } else map.set(key, make());
    };

    for (let k = 0; k < n; k++) {
        const here = pages[k], next = pages[k + 1], prev = pages[k - 1];
        const installed: K[] = [], hovering: K[] = [];
        for (const [p, v] of here) {
            if (!last.has(p)) continue;
            if (!v.pose.part.solids.some(s => s.shape.closed)) open.add(p);
            if (v.pose.key === last.get(p)!.pose.key) installed.push(p);
            else if (next?.has(p) && next.get(p)!.pose.key !== v.pose.key && (firstPage.get(p) === k || prev?.get(p)?.pose.key !== v.pose.key)) hovering.push(p);
        }
        if (input.only !== undefined && k !== input.only) continue;
        const part = (p: K): FitPart => here.get(p)!.pose.part;
        const meetHere = (x: K, y: K): FitContact => meet(x, here.get(x)!.pose, y, here.get(y)!.pose);
        const newer = (x: K, y: K): [K, K] => {
            const fx = firstPage.get(x)!, fy = firstPage.get(y)!;
            if (fx !== fy) return fx > fy ? [x, y] : [y, x];
            // Added together (or no pages): move the one on top.
            const cx = part(x).lo[1] + part(x).hi[1], cy = part(y).lo[1] + part(y).hi[1];
            if (Math.abs(cx - cy) > 1e-6 * size) return cx > cy ? [x, y] : [y, x];
            return volume(part(x)) <= volume(part(y)) ? [x, y] : [y, x];
        };
        /** Where `p` lands if moved by `d` in the frame: in its parent, and as a distance. */
        const asMove = (p: K, d: Vec3, axis: number): Move => {
            const v = here.get(p)!, m = v.toParent;
            const by = [0, 1, 2].map(c => d[0] * m[c] + d[1] * m[3 + c] + d[2] * m[6 + c]) as Vec3;
            return { by, position: v.position.map((x, i) => x + by[i]) as Vec3, mm: Math.hypot(...d) * 1000, axis, sign: Math.sign(d[axis]) };
        };
        /** Seat `p` along `axis` and `sign` among the parts installed on this page: the move, if it makes one. */
        const seatMove = (p: K | K[], axis: number, sign: number): { move: Move | null; moves: Move[] | null; under: K | null; drop: number; nothing: boolean } => {
            const group = Array.isArray(p) ? p : [p];
            const parts = group.map(part);
            const solids = parts.flatMap(x => x.solids), knobs = parts.flatMap(x => x.knobs);
            const lo = [0, 1, 2].map(i => Math.min(...parts.map(x => x.lo[i]))) as Vec3, hi = [0, 1, 2].map(i => Math.max(...parts.map(x => x.hi[i]))) as Vec3;
            const mover: FitPart = parts.length === 1 ? parts[0] : { ...parts[0], solids, knobs, lo, hi, body: { lo, hi }, knob: null };
            const others = new Map(installed.filter(q => !group.includes(q)).map(q => [q, part(q)]));
            const dir: Vec3 = [0, 0, 0];
            dir[axis] = sign;
            const sweep = sweepContact(mover, others, dir);
            const d: Vec3 = [0, 0, 0];
            d[axis] = sign * sweep.t;
            const moving = !sweep.nothing && Math.abs(sweep.t) * 1000 >= MOVE_MIN_MM;
            const move = moving && group.length === 1 ? asMove(group[0], d, axis) : null;
            const moves = moving && group.length > 1 ? group.map(q => asMove(q, d, axis)) : null;
            return { move, moves, under: sweep.contacts[0]?.part ?? null, drop: sweep.t, nothing: sweep.nothing };
        };
        /**
         * Seat `moved` toward `into` along `axis`: it backs out of `into` (or slides to contact), studs engaged. A
         * move longer than the part (through the model to the far side), or than `limit`, is no fix: then none is
         * offered, and the finding says to check which part is misplaced, or that one may clip over the other.
         */
        const clearMove = (moved: K, into: K, axis: number, limit = Infinity): Move | null => {
            const a = part(moved), b = part(into);
            const towards = (b.lo[axis] + b.hi[axis]) - (a.lo[axis] + a.hi[axis]);
            const s = seatMove(moved, axis, towards > 0 ? 1 : -1);
            return s.move && Math.abs(s.drop) <= Math.min(a.hi[axis] - a.lo[axis], limit) ? s.move : null;
        };
        const supportOf = (group: K[]): Omit<Floating<K>, 'parts' | 'pages'> => {
            const s = seatMove(group, 1, -1);
            // Runs through it: their boxes overlap on every axis, and the support is the narrower across the drop on
            // one axis, strictly inside it (a rim, a pin); a support round it (a part above the floor of a U, even as deep) is not.
            const under = s.under === null ? null : part(s.under);
            const through = !!under && group.some(p => {
                const q = part(p);
                return [0, 1, 2].every(k => under.lo[k] < q.hi[k] && under.hi[k] > q.lo[k]) && [0, 2].some(k => { const tol = 0.01 * (q.hi[k] - q.lo[k]); return under.lo[k] > q.lo[k] + tol && under.hi[k] < q.hi[k] - tol; });
            });
            // On another side, within half its size and nearer than what is under it: it may be held there, not from below.
            let side: Floating<K>['side'] = null;
            for (const [axis, sign] of [[0, 1], [0, -1], [1, 1], [2, 1], [2, -1]]) {
                const x = seatMove(group, axis, sign);
                if (!x.nothing && x.under !== null && x.drop > 0 && x.drop < (side?.gap ?? Math.min(0.5 * union(group.map(part)), s.nothing || s.under === null ? Infinity : s.drop))) side = { part: x.under, gap: x.drop, axis, sign, move: x.move, moves: x.moves };
            }
            return { under: s.under, drop: s.nothing ? 0 : s.drop, move: through ? null : s.move, moves: through ? null : s.moves, through, side };
        };

        const touching = new Map<K, Set<K>>(installed.map(p => [p, new Set()]));
        const link = (g: Map<K, Set<K>>, x: K, y: K): void => { g.get(x)!.add(y); g.get(y)!.add(x); };
        for (let i = 0; i < installed.length; i++) {
            for (let j = i + 1; j < installed.length; j++) {
                const x = installed[i], y = installed[j], a = part(x), b = part(y), t = tol(a, b);
                if (boxGap(a, b) > t.reach) continue;
                const c = meetHere(x, y), o = c.overlap;
                if (o || c.touch > 0 || c.flush > 0 || c.gap <= t.reach) link(touching, x, y);
                const key = pairKey(x, y);
                if (o && deep(o, a, b)) {
                    const kind = engagement(o);
                    if (kind) add(byDesign, key, k, () => ({ a: x, b: y, kind, pages: [k] }));
                    else add(sunk, key, k, () => {
                        const [moved, into] = newer(x, y);
                        const thick = part(moved).hi[o.axis] - part(moved).lo[o.axis];
                        // Backing out of an overlap moves about its depth (and a stud): much further lands it somewhere else.
                        return { moved, into, overlap: o, pages: [k], move: clearMove(moved, into, o.axis, 2 * o.depth + (stud ?? 0)), flush: c.flush > t.area,
                            whole: Math.abs(o.depth - thick) <= Math.max(0.1 * thick, t.step) };
                    });
                } else if (c.flush > t.area && !slabs.has(x) && !slabs.has(y) && !still.has(key)) {
                    if (!flicker.has(key)) {
                        const [moved, into] = newer(x, y);
                        const n = c.flushPlane!.normal;
                        const move = clearMove(moved, into, [0, 1, 2].reduce((m, i) => (Math.abs(n[i]) > Math.abs(n[m]) ? i : m), 0));
                        // Faces flush in a plane neither part needs to leave (resting side by side) don't flicker.
                        if (!move) { still.add(key); continue; }
                        flicker.set(key, { a: moved, b: into, area: c.flush, pages: [], move });
                    }
                    add(flicker, key, k, () => flicker.get(key)!);
                }
            }
        }
        const groups = (g: Map<K, Set<K>>): { parts: K[]; grounded: boolean }[] => {
            const seen = new Set<K>(), out: { parts: K[]; grounded: boolean; size: number }[] = [];
            for (const start of installed) {
                if (seen.has(start) || slabs.has(start)) continue;
                const parts: K[] = [], stack = [start];
                let grounded = false;
                seen.add(start);
                while (stack.length) {
                    const p = stack.pop()!;
                    parts.push(p);
                    for (const q of g.get(p)!) {
                        if (slabs.has(q)) { grounded = true; continue; }
                        if (!seen.has(q)) { seen.add(q); stack.push(q); }
                    }
                }
                out.push({ parts, grounded, size: parts.reduce((s, p) => s + (oversized.has(p) ? 0 : volume(part(p))), 0) });
            }
            const biggest = out.reduce<typeof out[number] | null>((m, x) => (!m || x.size > m.size ? x : m), null);
            if (biggest) biggest.grounded = true;
            return out;
        };
        const groupKey = (parts: K[]): string => parts.map(p => index.get(p)!).sort((x, y) => x - y).join(',');
        const loose = groups(touching).filter(g => !g.grounded);
        for (const g of loose) add(floating, groupKey(g.parts), k, () => ({ parts: g.parts, pages: [k], ...supportOf(g.parts) }));
        // A hovering part against anything that doesn't move with it to the next page.
        const motion = (p: K): Vec3 | null => {
            const a = here.get(p)!.pose.part, b = next?.get(p)?.pose.part;
            return b ? [0, 1, 2].map(i => b.lo[i] - a.lo[i]) as Vec3 : null;
        };
        for (const h of hovering) {
            const mh = motion(h)!;
            for (const [o, v] of here) {
                if (o === h || cues.has(o)) continue;
                const mo = motion(o);
                if (mo && Math.hypot(mo[0] - mh[0], mo[1] - mh[1], mo[2] - mh[2]) <= 1e-6 * size) continue;
                const a = part(h), b = v.pose.part;
                if (boxGap(a, b) > 0) continue;
                const ov = meetHere(h, o).overlap;
                if (ov && deep(ov, a, b) && !engagement(ov)) add(clash, `${index.get(h)}>${index.get(o)}`, k, () => ({ hovering: h, other: o, depth: ov.depth, pages: [k] }));
            }
        }
    }
    return {
        size, stud: grid, studded: model.filter(p => p.knob !== null).length, parts: last.size,
        skipped: { cues: cues.size, hovering: rest.size - last.size, open: [...open] },
        sunk: [...sunk.values()].sort((a, b) => b.overlap.depth - a.overlap.depth),
        flicker: [...flicker.values()].sort((a, b) => b.area - a.area),
        floating: mergeGroups([...floating.values()]),
        clash: [...clash.values()].sort((a, b) => b.depth - a.depth),
        byDesign: [...byDesign.values()],
        oversized: [...oversized.values()].filter(o => input.only === undefined || o.pages.includes(input.only)).sort((a, b) => b.across - a.across),
        firstPage, lastPage,
    };
}

/** Each part's set without it: the largest extent of the union of the others' boxes. */
function leaveOneOut(parts: readonly FitPart[]): number[] {
    const n = parts.length;
    type Box = { lo: number[]; hi: number[] };
    const pre: Box[] = [], suf: Box[] = [];
    const grow = (b: Box, p: FitPart): Box => ({ lo: b.lo.map((v, k) => Math.min(v, p.lo[k])), hi: b.hi.map((v, k) => Math.max(v, p.hi[k])) });
    const empty = { lo: [Infinity, Infinity, Infinity], hi: [-Infinity, -Infinity, -Infinity] };
    pre[0] = empty; for (let i = 0; i < n; i++) pre[i + 1] = grow(pre[i], parts[i]);
    suf[n] = empty; for (let i = n - 1; i >= 0; i--) suf[i] = grow(suf[i + 1], parts[i]);
    return parts.map((_, i) => {
        const lo = [0, 1, 2].map(k => Math.min(pre[i].lo[k], suf[i + 1].lo[k])), hi = [0, 1, 2].map(k => Math.max(pre[i].hi[k], suf[i + 1].hi[k]));
        return lo[0] === Infinity ? 0 : Math.max(hi[0] - lo[0], hi[1] - lo[1], hi[2] - lo[2]);
    });
}

/** One finding per group of parts that share a member across pages: the largest version, with all its pages. */
function mergeGroups<K>(list: Floating<K>[]): Floating<K>[] {
    const out: Floating<K>[] = [];
    for (const f of list) {
        const i = out.findIndex(m => m.parts.some(p => f.parts.includes(p)));
        if (i < 0) { out.push(f); continue; }
        const m = out[i];
        const pages = [...new Set([...m.pages, ...f.pages])].sort((a, b) => a - b);
        out[i] = { ...(f.parts.length > m.parts.length ? f : m), pages };
    }
    return out;
}
